import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, LogIn, Loader2, Tag, Info, ShieldAlert, X, Beef, MapPin, Scale, Hash, Coins, Eye } from 'lucide-react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getSystemSettings } from '../../services/configService';
import GuestTicketModal from '../../components/tickets/GuestTicketModal';
import { getParentBranchName } from '../../utils/branchUtils';

export default function PublicCatalogPage() {
  const navigate = useNavigate();
  const [catalogAnimals, setCatalogAnimals] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState('all');
  
  // Settings & Status
  const [systemSettings, setSystemSettings] = useState(null);
  
  // Ticket Modal State
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState(null);

  // Detay Modal State (KVKK uyumlu - sadece hayvan bilgileri)
  const [detailAnimal, setDetailAnimal] = useState(null);

  useEffect(() => {
    const initPage = async () => {
      try {
        const settings = await getSystemSettings();
        setSystemSettings(settings);
        
        // Sadece pazar açıksa ve bakım modu yoksa yükle (Yine de public sayfa olduğu için güvenli)
        if (!settings?.isCatalogClosed && !settings?.isSystemMaintenance) {
           await loadCatalog();
        }
      } catch (err) {
        console.error("Katalog yükleme hatası:", err);
      } finally {
        setCatalogLoading(false);
      }
    };
    initPage();
  }, []);

  const loadCatalog = async () => {
    try {
      const animalsSnap = await getDocs(query(collection(db, 'animals')));
      const allAnimals = animalsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      const sharesSnap = await getDocs(query(collection(db, 'shares')));
      const countMap = {};
      sharesSnap.docs.forEach(d => {
        const data = d.data();
        const animalId = data.animalId;
        const sharesBought = Number(data.shareWeight) || 1;
        countMap[animalId] = (countMap[animalId] || 0) + sharesBought;
      });
      
      const processed = allAnimals.map(anim => {
         const taken = countMap[anim.id] || 0;
         const remaining = anim.totalShares - taken;
         return { ...anim, takenShares: taken, remainingShares: remaining };
      }).filter(anim => anim.remainingShares > 0); // Hissesi dolan hayvanları gizle
      
      // Sıralama Algoritması: Kesim sırasına göre.
      processed.sort((a, b) => {
         const numA = parseInt(a.slaughterNo) || 99999;
         const numB = parseInt(b.slaughterNo) || 99999;
         return numA - numB;
      });
      
      setCatalogAnimals(processed);
    } catch (e) {
      console.error("Hayvanları çekerken hata:", e);
    }
  };

  const handleOpenTicket = (e, animal) => {
     e.stopPropagation();
     setSelectedAnimal(animal);
     setIsTicketModalOpen(true);
  };

  const handleOpenDetail = (animal) => {
     setDetailAnimal(animal);
  };

  // ── Bakım Modu veya Katalog Kapalı Kontrolü ──
  if (!catalogLoading && (systemSettings?.isSystemMaintenance || systemSettings?.isCatalogClosed)) {
     return (
        <div className="min-h-screen bg-navy-950 flex flex-col items-center justify-center p-6 text-center">
           <ShieldAlert className="w-16 h-16 text-gold-500 mb-4" />
           <h2 className="text-2xl font-bold text-white mb-2">Pazarımız Şu Anda Kapalıdır</h2>
           <p className="text-navy-300 max-w-md mb-8">
              Kurbanlık hisse satışlarımız kotaların dolması veya sistem bakımı nedeniyle geçici/kalıcı olarak durdurulmuştur. İlginiz için teşekkür ederiz.
           </p>
           <button onClick={() => navigate('/login')} className="px-6 py-3 bg-navy-800 hover:bg-navy-700 text-white rounded-xl font-bold transition-all border border-navy-700">
              Giriş Ekranına Dön
           </button>
        </div>
     );
  }

  // ── Hisse yüzde hesaplama ──
  const getSharePercent = (animal) => {
     const total = animal.totalShares || 1;
     const taken = animal.takenShares || 0;
     return Math.round((taken / total) * 100);
  };

  return (
    <div className="min-h-screen bg-navy-950 flex flex-col relative text-white">
      {/* ── Deep Blue Background ── */}
      <div className="fixed inset-0 z-0 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-950 pointer-events-none"></div>

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 glass border-b border-navy-700/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 relative z-10">
              <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg border border-white/10 bg-navy-950/50 flex-shrink-0 flex items-center justify-center">
                 <img src="/images/angry_bull_logo.png" alt="Logo" className="w-full h-full object-cover" />
              </div>
              <div>
                <h1 className="text-sm font-extrabold text-white leading-tight tracking-wide">
                  HAŞİMOĞLU ÇİFTLİĞİ
                </h1>
                <p className="text-[10px] text-gold-400 font-bold uppercase tracking-widest">Katalog Ekranı</p>
              </div>
            </div>

            <div className="flex items-center relative z-10">
              <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-navy-950 bg-gold-500 hover:bg-gold-400 transition-all shadow-lg shadow-gold-500/20"
              >
                <LogIn className="w-4 h-4" />
                Müşteri Girişi
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Main ── */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-8 relative z-10">
         <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
               <h2 className="text-xl font-bold text-white flex items-center gap-2">
               <ShoppingCart className="w-6 h-6 text-gold-400" />
               Kurbanlık Pazarı
               </h2>
               <p className="text-navy-300 mt-1 text-sm bg-navy-900/40 p-2 rounded-lg inline-block border border-navy-700/50">
                  Aşağıdaki listeden boş hisseleri inceleyebilir, karta tıklayarak detayları görebilirsiniz.
               </p>
            </div>
         </div>

         {catalogLoading ? (
            <div className="flex flex-col items-center justify-center p-12 text-navy-400">
               <Loader2 className="w-8 h-8 animate-spin mb-4 text-gold-500" />
               <p>Katalog yükleniyor...</p>
            </div>
         ) : catalogAnimals.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center text-navy-300 border border-navy-700/50 shadow-inner">
               Sistemde henüz kurbanlık bulunmuyor.
            </div>
         ) : (
            <>
               {/* ── Dynamic Branch Filter ── */}
               <div className="flex gap-2 overflow-x-auto pb-4 mb-2 hide-scrollbar">
                  {['all', ...Array.from(new Set(catalogAnimals.map(a => getParentBranchName(a.branch, systemSettings?.systemBranches))))].map(branch => (
                     <button
                        key={branch}
                        onClick={() => setSelectedBranch(branch)}
                        className={`whitespace-nowrap px-4 py-2 rounded-xl text-sm font-bold transition-all shadow-md ${
                           selectedBranch === branch 
                           ? 'bg-gold-500 text-navy-950 scale-105 border border-gold-400' 
                           : 'bg-navy-800/80 text-navy-300 hover:text-white border border-navy-700 hover:bg-navy-700'
                        }`}
                     >
                        {branch === 'all' ? 'Tüm Şubeler' : branch}
                     </button>
                  ))}
               </div>

               <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                  {catalogAnimals
                     .filter(a => selectedBranch === 'all' || getParentBranchName(a.branch, systemSettings?.systemBranches) === selectedBranch)
                     .map(animal => {
                     const isFull = animal.remainingShares <= 0;
                     const isLastOne = animal.remainingShares === 1;
                     const isLastTwo = animal.remainingShares === 2;
                     
                     return (
                        <div 
                           key={animal.id} 
                           onClick={() => handleOpenDetail(animal)}
                           className="glass rounded-2xl overflow-hidden border border-navy-700/60 flex relative hover:bg-navy-800/40 transition-all shadow-lg hover:shadow-gold-500/10 cursor-pointer group hover:border-gold-500/40 hover:scale-[1.01] duration-300"
                        >
                        {/* Tıkla Detay Gör Overlay */}
                        <div className="absolute inset-0 z-20 bg-navy-950/0 group-hover:bg-navy-950/20 transition-colors pointer-events-none flex items-center justify-center opacity-0 group-hover:opacity-100 duration-300">
                           <span className="bg-navy-900/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full border border-navy-600 shadow-lg flex items-center gap-1.5">
                              <Eye className="w-3.5 h-3.5" /> Detayları Gör
                           </span>
                        </div>

                        {/* Thumbnail Area */}
                        <div className="w-28 sm:w-32 shrink-0 bg-navy-900/80 relative flex flex-col items-center justify-center border-r border-navy-700/50">
                           {animal.photoUrl ? (
                              <img src={animal.photoUrl} alt={animal.tagNo} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
                           ) : (
                              <div className="absolute inset-0 flex items-center justify-center text-4xl opacity-20">🐄</div>
                           )}
                           <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy-950 to-transparent p-2 text-center">
                              <span className="text-white font-black text-lg drop-shadow-md">{animal.tagNo}</span>
                           </div>
                        </div>

                        {/* Details Area */}
                        <div className="p-3 sm:p-4 flex-1 flex flex-col justify-between relative z-10 w-full">
                           {/* Status Rose */}
                           <div className="flex justify-between items-start mb-2">
                              {/* Şube Etiketi */}
                              <span className="text-navy-300 text-[10px] font-bold bg-navy-900/50 px-2 py-0.5 rounded border border-navy-700/50 flex items-center gap-1">
                                 <span className="text-red-400">📍</span> {getParentBranchName(animal.branch, systemSettings?.systemBranches)}
                              </span>

                              {/* FOMO Aciliyet Rozetleri */}
                              {isFull ? (
                                 <span className="bg-navy-800 text-navy-500 border border-navy-700 text-[10px] font-bold px-2 py-0.5 rounded-full">TÜKENDİ</span>
                              ) : isLastOne ? (
                                 <span className="bg-danger-500/90 text-white border border-danger-400 text-[10px] items-center flex gap-1 font-bold px-2 py-0.5 rounded-full animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.6)]">
                                    <ShieldAlert className="w-3 h-3" /> 🚨 SADECE SON HİSSE!
                                 </span>
                              ) : isLastTwo ? (
                                 <span className="bg-gold-500/90 text-navy-950 border border-gold-400 text-[10px] items-center flex gap-1 font-bold px-2 py-0.5 rounded-full animate-pulse shadow-[0_0_10px_rgba(234,179,8,0.5)]">
                                    <Tag className="w-3 h-3" /> 🔥 SON 2 HİSSE
                                 </span>
                              ) : (
                                 <span className="bg-forest-500/20 text-forest-300 border border-forest-500/30 text-[10px] items-center flex gap-1 font-bold px-2 py-0.5 rounded-full shadow-md shadow-forest-500/10 transition-all hover:bg-forest-500/30">
                                    <Tag className="w-3 h-3" /> {animal.remainingShares} BOŞ HİSSE
                                 </span>
                              )}
                           </div>

                           <div className="flex flex-wrap gap-2 text-[11px] mb-3">
                              <div className="bg-navy-900/60 px-2 py-1 rounded-md border border-navy-700/50 flex flex-col">
                                 <span className="text-navy-400">Sıra</span>
                                 <span className="text-white font-bold">{animal.slaughterNo || '-'}</span>
                              </div>
                              <div className="bg-navy-900/60 px-2 py-1 rounded-md border border-navy-700/50 flex flex-col">
                                 <span className="text-navy-400">Ağırlık</span>
                                 <span className="text-white font-bold">{animal.weight ? `${animal.weight} KG` : '-'}</span>
                              </div>
                              <div className="bg-navy-900/60 px-2 py-1 rounded-md border border-navy-700/50 flex flex-col flex-1 pl-3 border-l-2 border-l-gold-500">
                                 <span className="text-navy-400">Hisse Fiyatı</span>
                                 <span className="text-gold-400 font-black text-sm">₺{(Number(animal.price || 0) / Number(animal.totalShares || 1)).toLocaleString('tr-TR')}</span>
                              </div>
                           </div>

                           {/* Satın Al / İletişim Butonu */}
                           <div className="mt-auto pt-1">
                              <button
                                 onClick={(e) => handleOpenTicket(e, animal)}
                                 disabled={isFull}
                                 className={`w-full py-2.5 rounded-xl text-sm font-extrabold flex items-center justify-center gap-2 transition-all relative z-30 ${
                                    isFull 
                                    ? 'bg-navy-800 text-navy-500 cursor-not-allowed border border-navy-700' 
                                    : 'bg-gold-500 text-navy-950 shadow-[0_0_15px_rgba(234,179,8,0.3)] hover:scale-[1.02] active:scale-[0.98]'
                                 }`}
                              >
                                 <Info className="w-4 h-4" />
                                 {isFull ? 'Hisseler Tükendi' : 'Bilgi Al & İletişim'}
                              </button>
                           </div>
                        </div>
                     </div>
                  )
               })}
            </div>
            </>
         )}
      </main>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ── HAYVAN DETAY MODALI (KVKK Uyumlu – Kişisel Veri Yok) ──  */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {detailAnimal && (() => {
         const animal = detailAnimal;
         const isFull = animal.remainingShares <= 0;
         const isLastOne = animal.remainingShares === 1;
         const isLastTwo = animal.remainingShares === 2;
         const sharePercent = getSharePercent(animal);
         const sharePrice = Math.round(Number(animal.price || 0) / Number(animal.totalShares || 1));
         const branchName = getParentBranchName(animal.branch, systemSettings?.systemBranches);

         return (
            <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" onClick={() => setDetailAnimal(null)}>
               <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
               
               <div 
                  className="relative w-full max-w-lg glass border border-navy-700 rounded-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]"
                  onClick={(e) => e.stopPropagation()}
               >
                  {/* ── Modal Header ── */}
                  <div className="px-5 py-4 border-b border-navy-700/50 bg-navy-900 flex justify-between items-center shrink-0">
                     <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center border border-gold-500/30">
                           <Beef className="w-4 h-4 text-gold-400" />
                        </div>
                        <div>
                           <h3 className="text-base font-bold text-white">Kurbanlık Detayları</h3>
                           <p className="text-[10px] text-navy-400 font-medium">Küpe No: {animal.tagNo}</p>
                        </div>
                     </div>
                     <button 
                        onClick={() => setDetailAnimal(null)} 
                        className="text-navy-400 hover:text-white p-2 rounded-lg hover:bg-navy-800 transition-colors"
                     >
                        <X className="w-5 h-5" />
                     </button>
                  </div>

                  {/* ── Modal Body ── */}
                  <div className="flex-1 overflow-y-auto">
                     
                     {/* Fotoğraf Alanı */}
                     <div className="w-full aspect-[16/10] bg-navy-900 relative overflow-hidden">
                        {animal.photoUrl ? (
                           <img src={animal.photoUrl} alt={animal.tagNo} className="w-full h-full object-cover" />
                        ) : (
                           <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-b from-navy-900 to-navy-950">
                              <span className="text-7xl opacity-30 mb-3">🐄</span>
                              <span className="text-xs text-navy-500 font-medium uppercase tracking-wider">Fotoğraf Yüklenmemiş</span>
                           </div>
                        )}
                        {/* Gradient Overlay */}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy-950 via-navy-950/50 to-transparent h-24" />
                        
                        {/* Şube Rozeti */}
                        <div className="absolute top-3 left-3">
                           <span className="bg-navy-900/80 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-lg border border-navy-600/50 shadow-lg flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-gold-400" /> {branchName}
                           </span>
                        </div>

                        {/* Durum Rozeti */}
                        <div className="absolute top-3 right-3">
                           {isFull ? (
                              <span className="bg-navy-800/90 backdrop-blur-sm text-navy-400 border border-navy-600 text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg">TÜKENDİ</span>
                           ) : isLastOne ? (
                              <span className="bg-danger-500/90 backdrop-blur-sm text-white border border-danger-400 text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg shadow-danger-500/30 animate-pulse flex items-center gap-1.5">
                                 🚨 SON HİSSE!
                              </span>
                           ) : isLastTwo ? (
                              <span className="bg-gold-500/90 backdrop-blur-sm text-navy-950 border border-gold-400 text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg shadow-gold-500/30 animate-pulse flex items-center gap-1.5">
                                 🔥 SON 2 HİSSE
                              </span>
                           ) : (
                              <span className="bg-forest-500/90 backdrop-blur-sm text-white border border-forest-400 text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg shadow-forest-500/20 flex items-center gap-1.5">
                                 <Tag className="w-3.5 h-3.5" /> {animal.remainingShares} BOŞ HİSSE
                              </span>
                           )}
                        </div>

                        {/* Küpe No (Alt üst) */}
                        <div className="absolute bottom-3 left-4">
                           <p className="text-2xl font-black text-white drop-shadow-lg tracking-wide">{animal.tagNo}</p>
                           <p className="text-[10px] text-navy-300 font-bold uppercase tracking-widest mt-0.5">KÜPE NUMARASI</p>
                        </div>
                     </div>

                     {/* Bilgi Kartları */}
                     <div className="p-5 space-y-4">
                        
                        {/* Üst Bilgi Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                           <div className="bg-navy-800/50 p-3 rounded-xl border border-navy-700/50 text-center">
                              <div className="flex items-center justify-center mb-1.5">
                                 <Hash className="w-4 h-4 text-forest-400" />
                              </div>
                              <p className="text-[10px] text-navy-400 uppercase tracking-wider font-semibold mb-0.5">Hayvan No</p>
                              <p className="text-lg font-bold text-white">{animal.animalNo || '-'}</p>
                           </div>
                           <div className="bg-navy-800/50 p-3 rounded-xl border border-navy-700/50 text-center">
                              <div className="flex items-center justify-center mb-1.5">
                                 <Beef className="w-4 h-4 text-gold-400" />
                              </div>
                              <p className="text-[10px] text-navy-400 uppercase tracking-wider font-semibold mb-0.5">Kesim Sırası</p>
                              <p className="text-lg font-bold text-forest-400">{animal.slaughterNo || '-'}</p>
                           </div>
                           <div className="bg-navy-800/50 p-3 rounded-xl border border-navy-700/50 text-center">
                              <div className="flex items-center justify-center mb-1.5">
                                 <Scale className="w-4 h-4 text-cyan-400" />
                              </div>
                              <p className="text-[10px] text-navy-400 uppercase tracking-wider font-semibold mb-0.5">Ağırlık</p>
                              <p className="text-lg font-bold text-white">{animal.weight ? `${animal.weight}` : '-'} <span className="text-xs text-navy-400 font-normal">KG</span></p>
                           </div>
                           <div className="bg-navy-800/50 p-3 rounded-xl border border-navy-700/50 text-center">
                              <div className="flex items-center justify-center mb-1.5">
                                 <Tag className="w-4 h-4 text-purple-400" />
                              </div>
                              <p className="text-[10px] text-navy-400 uppercase tracking-wider font-semibold mb-0.5">Tür</p>
                              <p className="text-lg font-bold text-white capitalize">{animal.type === 'buyukbas' ? 'Büyükbaş' : 'Küçükbaş'}</p>
                           </div>
                        </div>

                        {/* Fiyat ve Hisse Bilgisi */}
                        <div className="grid grid-cols-2 gap-3">
                           <div className="bg-gradient-to-br from-gold-500/10 to-gold-700/5 p-4 rounded-xl border border-gold-500/30">
                              <div className="flex items-center gap-2 mb-2">
                                 <Coins className="w-4 h-4 text-gold-400" />
                                 <p className="text-[10px] text-gold-400/80 uppercase tracking-wider font-bold">Hisse Bedeli</p>
                              </div>
                              <p className="text-2xl font-black text-gold-400">{sharePrice.toLocaleString('tr-TR')} <span className="text-sm font-bold">₺</span></p>
                              <p className="text-[10px] text-navy-400 mt-1">Toplam: {Number(animal.price || 0).toLocaleString('tr-TR')} ₺ / {animal.totalShares || 7} hisse</p>
                           </div>
                           <div className="bg-navy-800/50 p-4 rounded-xl border border-navy-700/50">
                              <p className="text-[10px] text-navy-400 uppercase tracking-wider font-bold mb-2">Hisse Durumu</p>
                              <div className="flex items-end gap-2 mb-2">
                                 <span className={`text-2xl font-black ${isFull ? 'text-danger-400' : 'text-forest-400'}`}>
                                    {animal.remainingShares}
                                 </span>
                                 <span className="text-sm text-navy-400 mb-0.5">/ {animal.totalShares} boş</span>
                              </div>
                              <div className="h-2.5 w-full bg-navy-700 rounded-full overflow-hidden">
                                 <div 
                                    className={`h-full rounded-full transition-all duration-700 ${sharePercent >= 100 ? 'bg-danger-500' : sharePercent >= 70 ? 'bg-gold-500' : 'bg-forest-500'}`}
                                    style={{ width: `${sharePercent}%` }}
                                 />
                              </div>
                              <p className="text-[10px] text-navy-500 mt-1.5">%{sharePercent} dolu</p>
                           </div>
                        </div>

                     </div>
                  </div>

                  {/* ── Modal Footer ── */}
                  <div className="px-5 py-4 border-t border-navy-700/50 bg-navy-900/50 flex gap-3 shrink-0">
                     <button
                        onClick={() => setDetailAnimal(null)}
                        className="flex-1 py-3 bg-navy-800 hover:bg-navy-700 text-white rounded-xl transition-colors text-sm font-semibold border border-navy-600"
                     >
                        Kapat
                     </button>
                     {!isFull && (
                        <button
                           onClick={() => {
                              setDetailAnimal(null);
                              setSelectedAnimal(animal);
                              setIsTicketModalOpen(true);
                           }}
                           className="flex-[2] py-3 bg-gold-500 hover:bg-gold-400 text-navy-950 rounded-xl transition-all text-sm font-extrabold flex items-center justify-center gap-2 shadow-lg shadow-gold-500/20"
                        >
                           <Info className="w-4 h-4" />
                           Bilgi Al & İletişim
                        </button>
                     )}
                  </div>
               </div>
            </div>
         );
      })()}

      {/* Guest Ticket Modal */}
      <GuestTicketModal
         isOpen={isTicketModalOpen}
         onClose={() => setIsTicketModalOpen(false)}
         animal={selectedAnimal}
      />
    </div>
  );
}
